import { checkScheduleAssignments } from './jobIntegrity';
import { estimateTravelKm } from './travelTime';
import { validateVehicleChain } from './validateVehicleChain';
import type {
  DailySchedulePlan,
  PlanDailyScheduleInput,
  ScheduleJobInput,
  ScheduleLegInput,
  SchedulePinFailure,
  ScheduleVehicleInput,
  VehicleChainPlan,
} from './types';
import type { ScheduleValidationIssue } from './validationIssues';

const NEW_VEHICLE_PENALTY_KM = 50;

interface LegWorkItem {
  job: ScheduleJobInput;
  leg: ScheduleLegInput;
}

function flattenLegs(jobs: ScheduleJobInput[]): LegWorkItem[] {
  const items: LegWorkItem[] = [];
  for (const job of jobs) {
    for (const leg of job.legs) {
      items.push({ job, leg });
    }
  }
  return items.sort(
    (a, b) => a.leg.earliestStartMinutes - b.leg.earliestStartMinutes
  );
}

function insertLegAt(
  chain: ScheduleLegInput[],
  leg: ScheduleLegInput,
  position: number
): ScheduleLegInput[] {
  return [...chain.slice(0, position), leg, ...chain.slice(position)];
}

function insertLegSorted(chain: ScheduleLegInput[], leg: ScheduleLegInput): ScheduleLegInput[] {
  const next = [...chain, leg];
  next.sort((a, b) => a.earliestStartMinutes - b.earliestStartMinutes);
  return next;
}

function vehicleFitsJob(vehicle: ScheduleVehicleInput, job: ScheduleJobInput): boolean {
  if (vehicle.capacity < job.passengerCount) return false;
  if (
    job.fleetOperatorId &&
    vehicle.fleetOperatorId !== job.fleetOperatorId
  ) {
    return false;
  }
  return true;
}

/**
 * Place all legs of a job onto one vehicle (best deadhead). Used to recover RT jobs
 * left unassigned by leg-greedy insertion.
 */
function tryPlaceWholeJob(
  job: ScheduleJobInput,
  chains: Map<string, ScheduleLegInput[]>,
  vehicles: ScheduleVehicleInput[],
  jobVehicle: Map<string, string>,
  travelOptions: PlanDailyScheduleInput['travelOptions']
): boolean {
  if (jobVehicle.has(job.id) || job.legs.length === 0) return false;

  const sortedLegs = [...job.legs].sort(
    (a, b) => a.earliestStartMinutes - b.earliestStartMinutes
  );
  let best: { vehicleId: string; chain: ScheduleLegInput[]; score: number } | null = null;

  for (const vehicle of vehicles) {
    if (!vehicleFitsJob(vehicle, job)) continue;
    let candidate = [...(chains.get(vehicle.id) ?? [])];
    for (const leg of sortedLegs) {
      candidate = insertLegSorted(candidate, { ...leg, isFixed: false });
    }
    const validation = validateVehicleChain(candidate, travelOptions);
    if (!validation.ok) continue;
    const isNew = (chains.get(vehicle.id) ?? []).length === 0;
    const score =
      validation.totalDeadheadKm + (isNew ? NEW_VEHICLE_PENALTY_KM * 0.25 : 0);
    if (!best || score < best.score) {
      best = { vehicleId: vehicle.id, chain: candidate, score };
    }
  }

  if (!best) return false;
  chains.set(best.vehicleId, best.chain);
  jobVehicle.set(job.id, best.vehicleId);
  return true;
}

function linkScore(chain: ScheduleLegInput[], leg: ScheduleLegInput, position: number): number {
  const before = chain[position - 1];
  if (!before) return 0;
  return estimateTravelKm(before.dropoff, leg.pickup);
}

/** True when this vehicle already runs part of a multi-leg job whose later legs are not yet placed. */
function vehicleHasOpenMultiLegJob(
  vehicleId: string,
  chains: Map<string, ScheduleLegInput[]>,
  jobVehicle: Map<string, string>,
  jobsById: Map<string, ScheduleJobInput>
): boolean {
  for (const [jobId, assignedVehicleId] of jobVehicle) {
    if (assignedVehicleId !== vehicleId) continue;
    const job = jobsById.get(jobId);
    if (!job || job.legs.length <= 1) continue;
    const chain = chains.get(vehicleId) ?? [];
    const placed = new Set(chain.filter((l) => l.jobId === jobId).map((l) => l.legId));
    if (job.legs.some((l) => !placed.has(l.legId))) return true;
  }
  return false;
}

function removeJobFromChains(
  jobId: string,
  chains: Map<string, ScheduleLegInput[]>,
  jobVehicle: Map<string, string>
): void {
  const vehicleId = jobVehicle.get(jobId);
  if (!vehicleId) return;
  const chain = chains.get(vehicleId) ?? [];
  const next = chain.filter((l) => l.isFixed || l.jobId !== jobId);
  if (next.length === 0) chains.delete(vehicleId);
  else chains.set(vehicleId, next);
  jobVehicle.delete(jobId);
}

function buildChainPlan(
  vehicleId: string,
  legs: ScheduleLegInput[],
  travelOptions: PlanDailyScheduleInput['travelOptions'],
  fleetNumber?: string
): VehicleChainPlan {
  const validation = validateVehicleChain(legs, travelOptions);
  return { vehicleId, legs, validation, fleetNumber };
}

interface InsertCandidate {
  vehicleId: string;
  position: number;
  chain: ScheduleLegInput[];
  score: number;
}

function findBestLegInsertion(
  leg: ScheduleLegInput,
  job: ScheduleJobInput,
  chains: Map<string, ScheduleLegInput[]>,
  vehicles: ScheduleVehicleInput[],
  jobVehicle: Map<string, string>,
  jobsById: Map<string, ScheduleJobInput>,
  travelOptions: PlanDailyScheduleInput['travelOptions']
): InsertCandidate | null {
  const assignedVehicle = jobVehicle.get(job.id);
  const candidateVehicles = assignedVehicle
    ? vehicles.filter((v) => v.id === assignedVehicle)
    : vehicles;

  let best: InsertCandidate | null = null;

  for (const vehicle of candidateVehicles) {
    if (!vehicleFitsJob(vehicle, job)) continue;

    const chain = chains.get(vehicle.id) ?? [];
    const isNewVehicle = chain.length === 0;

    for (let position = 0; position <= chain.length; position++) {
      const candidate = insertLegAt(chain, leg, position);
      const validation = validateVehicleChain(candidate, travelOptions);
      if (!validation.ok) continue;

      const linkKm = linkScore(chain, leg, position);
      let score =
        validation.totalDeadheadKm +
        linkKm * 0.1 +
        (isNewVehicle && !assignedVehicle ? NEW_VEHICLE_PENALTY_KM : 0);

      // Stand by must not occupy a bus that still owes later legs of another job.
      if (leg.legType === 'standby_block') {
        if (vehicleHasOpenMultiLegJob(vehicle.id, chains, jobVehicle, jobsById)) {
          continue;
        }
      }

      if (!best || score < best.score) {
        best = { vehicleId: vehicle.id, position, chain: candidate, score };
      }
    }
  }

  return best;
}

function tryLocalLegSwap(
  chains: Map<string, ScheduleLegInput[]>,
  jobA: ScheduleJobInput,
  jobB: ScheduleJobInput,
  vehicleA: string,
  vehicleB: string,
  vehicles: ScheduleVehicleInput[],
  travelOptions: PlanDailyScheduleInput['travelOptions']
): boolean {
  if (vehicleA === vehicleB) return false;

  const chainA = chains.get(vehicleA) ?? [];
  const chainB = chains.get(vehicleB) ?? [];
  const legsA = chainA.filter((l) => l.jobId === jobA.id);
  const legsB = chainB.filter((l) => l.jobId === jobB.id);
  if (legsA.length === 0 || legsB.length === 0) return false;

  const vehicleMetaA = vehicles.find((v) => v.id === vehicleA);
  const vehicleMetaB = vehicles.find((v) => v.id === vehicleB);
  if (!vehicleMetaA || !vehicleMetaB) return false;
  if (!vehicleFitsJob(vehicleMetaA, jobB) || !vehicleFitsJob(vehicleMetaB, jobA)) return false;

  const withoutA = chainA.filter((l) => l.isFixed || l.jobId !== jobA.id);
  const withoutB = chainB.filter((l) => l.isFixed || l.jobId !== jobB.id);

  let newA = withoutA;
  for (const leg of legsB) {
    newA = insertLegAt(newA, leg, newA.length);
  }
  let newB = withoutB;
  for (const leg of legsA) {
    newB = insertLegAt(newB, leg, newB.length);
  }

  const valA = validateVehicleChain(newA, travelOptions);
  const valB = validateVehicleChain(newB, travelOptions);
  if (!valA.ok || !valB.ok) return false;

  const oldKm =
    validateVehicleChain(chainA, travelOptions).totalDeadheadKm +
    validateVehicleChain(chainB, travelOptions).totalDeadheadKm;
  const newKm = valA.totalDeadheadKm + valB.totalDeadheadKm;
  if (newKm >= oldKm) return false;

  chains.set(vehicleA, newA);
  chains.set(vehicleB, newB);
  return true;
}

/**
 * Leg-level greedy + local-search daily link scheduler (EXT.SCHED Phase 2).
 * Jobs may be interleaved on the same vehicle (e.g. B→C→D→B return).
 */
export function planDailySchedule(input: PlanDailyScheduleInput): DailySchedulePlan {
  const travelOptions = input.travelOptions ?? {};
  const vehicleMap = new Map(input.vehicles.map((v) => [v.id, v]));
  const jobsById = new Map(input.jobs.map((j) => [j.id, j]));
  const chains = new Map<string, ScheduleLegInput[]>();
  const jobVehicle = new Map<string, string>();
  const failedLegs: string[] = [];

  const fixedByVehicle = input.fixedLegsByVehicle ?? {};
  for (const [vehicleId, fixedLegs] of Object.entries(fixedByVehicle)) {
    if (!vehicleMap.has(vehicleId) || fixedLegs.length === 0) continue;
    chains.set(
      vehicleId,
      fixedLegs.map((leg) => ({ ...leg, isFixed: true }))
    );
  }

  const eligibleJobs = input.jobs.filter((j) => j.legs.length > 0);
  const pinFailures: SchedulePinFailure[] = [];
  const successfulPins: Record<string, string> = {};
  const blockedByFailedPin = new Set<string>();
  const requestedPins = input.pinnedAssignments ?? {};

  for (const [jobId, vehicleId] of Object.entries(requestedPins)) {
    const job = jobsById.get(jobId);
    const vehicle = vehicleMap.get(vehicleId);
    if (!job || job.legs.length === 0 || !vehicle) {
      pinFailures.push({
        jobId,
        title: jobsById.get(jobId)?.title ?? jobId,
        issues: [{ code: 'JOB_NOT_IN_PLAN', params: { jobId } }],
      });
      blockedByFailedPin.add(jobId);
      continue;
    }
    if (vehicle.capacity < job.passengerCount) {
      pinFailures.push({
        jobId,
        title: job.title,
        issues: [
          {
            code: 'CAPACITY_SHORT',
            params: {
              vehicle: vehicle.fleetNumber ?? vehicleId,
              capacity: vehicle.capacity,
              passengers: job.passengerCount,
            },
          },
        ],
      });
      blockedByFailedPin.add(jobId);
      continue;
    }
    if (
      job.fleetOperatorId &&
      vehicle.fleetOperatorId !== job.fleetOperatorId
    ) {
      pinFailures.push({
        jobId,
        title: job.title,
        issues: [
          {
            code: 'FLEET_MISMATCH',
            params: {
              vehicle: vehicle.fleetNumber ?? vehicleId,
            },
          },
        ],
      });
      blockedByFailedPin.add(jobId);
      continue;
    }

    let chain = [...(chains.get(vehicleId) ?? [])];
    for (const leg of [...job.legs].sort(
      (a, b) => a.earliestStartMinutes - b.earliestStartMinutes
    )) {
      chain = insertLegSorted(chain, { ...leg, isFixed: false });
    }
    const validation = validateVehicleChain(chain, travelOptions);
    if (!validation.ok) {
      pinFailures.push({
        jobId,
        title: job.title,
        issues: validation.issues as ScheduleValidationIssue[],
      });
      blockedByFailedPin.add(jobId);
      continue;
    }
    chains.set(vehicleId, chain);
    jobVehicle.set(jobId, vehicleId);
    successfulPins[jobId] = vehicleId;
  }

  const legItems = flattenLegs(eligibleJobs);
  // Phase 1: transfers / school runs. Phase 2: Stand by fills leftover capacity.
  // Prevents Stand by from locking a bus that still owes a return leg (live ABCD seed).
  const orderedLegItems = [
    ...legItems.filter(({ leg }) => leg.legType !== 'standby_block'),
    ...legItems.filter(({ leg }) => leg.legType === 'standby_block'),
  ];

  for (const { job, leg } of orderedLegItems) {
    // Failed pins stay unassigned (CONF Q3) — do not auto-place elsewhere.
    if (blockedByFailedPin.has(job.id)) continue;
    const pinnedVehicle = jobVehicle.get(job.id);
    if (pinnedVehicle) {
      const chain = chains.get(pinnedVehicle) ?? [];
      if (chain.some((l) => l.legId === leg.legId)) continue;
    }
    const insertion = findBestLegInsertion(
      leg,
      job,
      chains,
      input.vehicles,
      jobVehicle,
      jobsById,
      travelOptions
    );
    if (!insertion) {
      failedLegs.push(leg.legId);
      // Drop partial morning legs so the job is not half-assigned under Stand by.
      // Do not rip out a successfully seeded pin.
      if (!successfulPins[job.id]) {
        removeJobFromChains(job.id, chains, jobVehicle);
      }
      continue;
    }
    chains.set(insertion.vehicleId, insertion.chain);
    jobVehicle.set(job.id, insertion.vehicleId);
  }

  let assignedJobIds = [...new Set([...jobVehicle.keys()])];
  const assignedJobs = eligibleJobs.filter((j) => assignedJobIds.includes(j.id));
  const pinnedJobIds = new Set(Object.keys(successfulPins));

  let improved = true;
  while (improved) {
    improved = false;
    for (let i = 0; i < assignedJobs.length; i++) {
      for (let j = i + 1; j < assignedJobs.length; j++) {
        const jobA = assignedJobs[i];
        const jobB = assignedJobs[j];
        const vA = jobVehicle.get(jobA.id);
        const vB = jobVehicle.get(jobB.id);
        if (!vA || !vB || vA === vB) continue;
        if (pinnedJobIds.has(jobA.id) || pinnedJobIds.has(jobB.id)) continue;
        if (tryLocalLegSwap(chains, jobA, jobB, vA, vB, input.vehicles, travelOptions)) {
          jobVehicle.set(jobA.id, vB);
          jobVehicle.set(jobB.id, vA);
          improved = true;
        }
      }
    }
  }

  // Second pass: whole-job placement for leftovers (esp. round-trip school runs).
  const retryJobs = eligibleJobs
    .filter((j) => !jobVehicle.has(j.id) && !blockedByFailedPin.has(j.id))
    .sort((a, b) => b.passengerCount - a.passengerCount);
  for (const job of retryJobs) {
    if (tryPlaceWholeJob(job, chains, input.vehicles, jobVehicle, travelOptions)) {
      for (const leg of job.legs) {
        const idx = failedLegs.indexOf(leg.legId);
        if (idx >= 0) failedLegs.splice(idx, 1);
      }
    }
  }

  assignedJobIds = [...new Set([...jobVehicle.keys()])];

  const vehiclePlans: VehicleChainPlan[] = [];
  for (const [vehicleId, legs] of chains) {
    if (legs.length === 0) continue;
    const meta = vehicleMap.get(vehicleId);
    vehiclePlans.push(buildChainPlan(vehicleId, legs, travelOptions, meta?.fleetNumber));
  }

  vehiclePlans.sort((a, b) => {
    const aStart = a.legs[0]?.earliestStartMinutes ?? 0;
    const bStart = b.legs[0]?.earliestStartMinutes ?? 0;
    return aStart - bStart;
  });

  const chainAssignments = vehiclePlans.map((v) => ({
    vehicleId: v.vehicleId,
    legs: v.legs,
  }));

  const allJobIds = eligibleJobs.map((j) => j.id);
  const integrity = checkScheduleAssignments(allJobIds, chainAssignments);

  const jobsWithFailedLegs = new Set(
    failedLegs
      .map((legId) => legItems.find((i) => i.leg.legId === legId)?.job.id)
      .filter(Boolean) as string[]
  );

  const unassignedJobIds = [
    ...new Set([
      ...integrity.unassignedJobIds,
      ...allJobIds.filter((id) => !assignedJobIds.includes(id)),
      ...[...jobsWithFailedLegs],
      ...blockedByFailedPin,
    ]),
  ];

  const unassignedJobs = unassignedJobIds.map((id) => ({
    id,
    title: jobsById.get(id)?.title ?? id,
  }));

  const totalDeadheadKm = vehiclePlans.reduce(
    (sum, v) => sum + v.validation.totalDeadheadKm,
    0
  );

  return {
    vehicles: vehiclePlans,
    unassignedJobIds,
    unassignedJobs,
    duplicateJobs: integrity.duplicateJobs,
    metrics: {
      vehicleCount: vehiclePlans.length,
      assignedJobCount: allJobIds.length - unassignedJobIds.length,
      totalDeadheadKm,
      allFeasible:
        vehiclePlans.every((v) => v.validation.ok) && unassignedJobIds.length === 0,
    },
    jobs: eligibleJobs,
    fleet: input.vehicles,
    travelOptions,
    fixedLegsByVehicle: fixedByVehicle,
    jobTitles: {
      ...Object.fromEntries(eligibleJobs.map((j) => [j.id, j.title])),
      ...(input.jobTitles ?? {}),
    },
    pins: successfulPins,
    pinFailures: pinFailures.length > 0 ? pinFailures : undefined,
  };
}

export function planToPublishPayload(plan: DailySchedulePlan): {
  assignments: Array<{
    vehicle_id: string;
    driver_id?: string;
    legs: Array<{ job_id: string; job_leg_id: string; sequence: number }>;
  }>;
} {
  const assignments = plan.vehicles
    .map((v) => {
      const publishable = v.legs.filter((leg) => !leg.isFixed);
      return {
        vehicle_id: v.vehicleId,
        ...(v.driverId ? { driver_id: v.driverId } : {}),
        legs: publishable.map((leg, index) => ({
          job_id: leg.jobId,
          job_leg_id: leg.legId,
          sequence: index + 1,
        })),
      };
    })
    .filter((a) => a.legs.length > 0);

  return { assignments };
}

/** Distinct non-fixed jobs that would be created as trips on publish. */
export function countPublishableJobs(plan: DailySchedulePlan): number {
  const ids = new Set<string>();
  for (const v of plan.vehicles) {
    for (const leg of v.legs) {
      if (!leg.isFixed) ids.add(leg.jobId);
    }
  }
  return ids.size;
}
